import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button/Button';
import { SchedulingBlockedTooltip } from '@/features/scheduling/components/SchedulingPaused/SchedulingBlockedTooltip';
import { useCanSchedule } from '@/features/scheduling/hooks/useCanSchedule';
import { useIsSchedulingBlocked } from '@/features/systemBanner/hooks/useIsSchedulingBlocked';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/utils';
import { useQuarterTick } from '../../hooks/useQuarterTick';

import readingSrc from '../../assets/reading.png';
import sphereSrc from '../../assets/sphere.png';
import workoutSrc from '../../assets/workout.png';
import { computeOpenToday, computeOpenTomorrow } from '../../utils/computeOpenHours';

interface HomeHeadingBoxProps {
  onScheduleClick: () => void;
  openButtonRef?: React.RefObject<HTMLButtonElement | null>;
}

export const HomeHeadingBox = ({ onScheduleClick, openButtonRef }: HomeHeadingBoxProps) => {
  const { t } = useTranslation('home', { keyPrefix: 'home' });

  const isTablet = useMediaQuery('tablet');

  const { userData } = useAuthStore();
  const { canSchedule } = useCanSchedule();
  const isSchedulingBlocked = useIsSchedulingBlocked();
  const isScheduling = canSchedule;

  const refreshTick = useQuarterTick();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const openToday = useMemo(() => computeOpenToday(userData?.center, new Date()), [userData?.center, refreshTick]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const openTomorrow = useMemo(() => computeOpenTomorrow(userData?.center, new Date()), [userData?.center, refreshTick]);

  if (!isTablet && isScheduling) {
    return null;
  }

  const computeImageSrc = () => {
    if (isScheduling) {
      return workoutSrc;
    }
    if (openToday.isOpenNow) {
      return readingSrc;
    }
    return sphereSrc;
  };

  const imageSrc = computeImageSrc();

  const computeHeadingText = () => {
    if (openToday.isOpenNow) {
      return t('dropInBox.openedHeading');
    }
    if (openToday.isOpenSoonToday) {
      return t('dropInBox.closedOpenTodayHeading');
    }
    if (openTomorrow.isOpenTomorrow) {
      return t('dropInBox.closedOpenTomorrowHeading');
    }
    if (!userData?.center?.hours?.officeHoursYext) {
      return userData?.center?.name ?? t('dropInBox.closedHeading');
    }
    return t('dropInBox.closedHeading');
  };
  const headingText = computeHeadingText();

  const computeSubheadingText = () => {
    if (openToday.isOpenNow) {
      return openToday.formattedLabel;
    }
    if (openToday.isOpenSoonToday) {
      return openToday.formattedLabel;
    }
    if (openTomorrow.isOpenTomorrow) {
      return openTomorrow.formattedLabel;
    }
    return null;
  };
  const subheadingText = computeSubheadingText();

  return (
    <div
      role='region'
      className={cn('bg-fill-background-light desktop:flex-[65] relative flex w-full items-center rounded-xl p-6', {
        'tablet:min-h-[210px]': isScheduling,
        'tablet:min-h-[180px]': !isScheduling,
      })}
      style={{
        background: isTablet ? `linear-gradient(211.12deg, ${openToday.isOpenNow ? '#F0FDF4' : '#FFF1F2'} 18.82%, #F6F6F6 59.85%)` : undefined,
      }}
    >
      {isScheduling ? (
        <div className='flex h-full flex-col items-start justify-around gap-4'>
          <p className='text-heading-m-medium max-w-1/2 [@media(min-width:1350px)]:max-w-[65%]'>{t('schedulingBox.title')}</p>
          <SchedulingBlockedTooltip isBlocked={isSchedulingBlocked}>
            <Button onClick={onScheduleClick} ref={openButtonRef} disabled={isSchedulingBlocked}>
              {t('schedulingBox.button.label')}
            </Button>
          </SchedulingBlockedTooltip>
        </div>
      ) : (
        <div className='tablet:max-w-1/2 tablet:flex-initial flex-1'>
          <div className='tablet:flex-col tablet:gap-4 tablet:items-start flex items-center gap-3'>
            <div
              aria-hidden
              className={cn('flex size-6 items-center justify-center rounded-full', {
                'bg-fill-status-red/8': !openToday.isOpenNow,
                'bg-fill-status-green/8': openToday.isOpenNow,
              })}
            >
              <div
                className={cn('size-3 rounded-full', {
                  'bg-fill-status-red': !openToday.isOpenNow,
                  'bg-fill-status-green': openToday.isOpenNow,
                })}
              />
            </div>
            <div className='flex flex-col gap-1'>
              <h2 className='text-heading-s-medium'>{headingText}</h2>
              {subheadingText && <p className='text-heading-s-medium'>{subheadingText}</p>}
            </div>
          </div>
          {openToday.isOpenNow && openTomorrow.isOpenTomorrow && (
            <div className='border-outline-neutral mt-6 border-t pt-4'>
              <p className='text-text-default-secondary'>{t('dropInBox.openedTomorrowDescription', { time: openTomorrow.formattedLabel })}</p>
            </div>
          )}
        </div>
      )}

      <div aria-hidden className='tablet:flex absolute top-1/2 right-1/12 hidden -translate-y-1/2 overflow-hidden'>
        <img alt='' src={imageSrc} className='w-[220px] [@media(min-width:1300px)]:w-[264px]' />
      </div>
    </div>
  );
};
